/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable } from '@angular/core';

import { D3ChartSvg, D3ChartSvgg, D3YScale, XAxisData } from '@shared/types/common-graph.types';
import { isDefined } from '@shared/utils/is-defined';
import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import {
  ChartBackgroundRectConfig,
  ChartBarConfig,
  ChartGroupedBarConfig,
  ChartStackedBarBarConfig,
  ChartStackedBarConfig,
  ChartXAxisConfig,
} from '../chart.view-model';
import { getBarData } from '../helpers/stacked-bar.helper';

@Injectable()
export class BarChartService<Data extends object> {
  public drawBackgroundRects(
    data: Data[],
    linesContainer: D3ChartSvgg,
    xAxisData: XAxisData,
    yScale: D3YScale,
    config: ChartBackgroundRectConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    if (data.length === 0) {
      return;
    }

    const minYPoint = yScale(yScale.domain()[0]);
    const maxYPoint = yScale(yScale.domain()[1]);

    data.forEach((d) => {
      if (config.backgroundFillColorClassFn) {
        const fillColorClass = config.backgroundFillColorClassFn(d);

        if (fillColorClass) {
          this.drawBackgroundRect(d, xAxisData, linesContainer, xAxisConfig, minYPoint, maxYPoint, fillColorClass, config);
        }
      }
    });
  }

  public drawBarChart(
    data: Data[],
    svg: D3ChartSvg,
    linesContainer: D3ChartSvgg,
    xAxisData: XAxisData,
    yScale: D3YScale,
    config: ChartBarConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    if (data.length === 0) {
      return;
    }

    const minYPoint = yScale(yScale.domain()[0]);
    const maxYPoint = yScale(yScale.domain()[1]);
    const zeroYPoint = yScale(0);
    let borderRadius = config.borderRadius ?? 4;
    const barPadding = config.barPadding ?? 2;
    const maxBarWidth = config.maxBarWidth ?? 16;
    const barWidth = Math.min(xAxisData.bandwidth - 2 * barPadding, maxBarWidth);
    const defs = svg.select('defs');

    if (barWidth < 2 * borderRadius) {
      borderRadius = barWidth / 2;
    }

    data.forEach((d, idx) => {
      const barXStartPoint = xAxisData.scale(d[xAxisConfig.dataKey] as number) - barWidth / 2;
      const uniqueId = getRuntimeUniqueId();
      const value = d[config.dataKey] as number | null | undefined;
      const barEndYPoint = yScale(value ?? 0);
      const barHeight = Math.abs(zeroYPoint - barEndYPoint);
      const barRadius = Math.min(borderRadius, barHeight);

      if (config.backgroundFillColorClassFn) {
        const fillColorClass = config.backgroundFillColorClassFn(d);

        if (fillColorClass) {
          this.drawBackgroundRect(d, xAxisData, linesContainer, xAxisConfig, minYPoint, maxYPoint, fillColorClass);
        }
      }

      defs
        .append('clipPath')
        .attr('id', uniqueId)
        .append('rect')
        .attr('x', barXStartPoint)
        .attr('y', 0)
        .attr('width', Math.max(barWidth, 0))
        .attr('height', Math.max(minYPoint - maxYPoint, 0));

      const barColorClass = typeof config.fillColorClass === 'function' ? config.fillColorClass(d, idx) : config.fillColorClass;
      const barPath =
        (value ?? 0) >= 0
          ? `
            M ${barXStartPoint} ${zeroYPoint}
            v ${-1 * (barHeight - barRadius)}
            q 0,${-1 * barRadius} ${barRadius},${-1 * barRadius}
            h ${barWidth - 2 * barRadius}
            q ${barRadius},0 ${barRadius},${barRadius}
            v ${barHeight - barRadius}
            z
            `
          : `
            M ${barXStartPoint} ${zeroYPoint}
            v ${barHeight - barRadius}
            q 0,${barRadius} ${barRadius},${barRadius}
            h ${barWidth - 2 * barRadius}
            q ${barRadius},0 ${barRadius},${-1 * barRadius}
            v ${-1 * (barHeight - barRadius)}
            z
            `;

      linesContainer.append('path').attr('d', barPath).attr('clip-path', `url(#${uniqueId})`).attr('class', barColorClass);

      if (config.valueLabelFormatter && isDefined(value)) {
        linesContainer
          .append('text')
          .attr('x', xAxisData.scale(d[xAxisConfig.dataKey] as number))
          .attr('y', barEndYPoint + (value < 0 ? 14 : -8))
          .attr('text-anchor', 'middle')
          .attr('class', 'chart-bar-value-label fill-foreground-primary-100 text-xs font-semibold')
          .text(config.valueLabelFormatter(value));
      }
    });
  }

  public drawStackedBarChart(
    data: Data[],
    svg: D3ChartSvg,
    linesContainer: D3ChartSvgg,
    xAxisData: XAxisData,
    yScale: D3YScale,
    config: ChartStackedBarConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    if (data.length === 0) {
      return;
    }

    const minYPoint = yScale(yScale.domain()[0]);
    const barWidth = this.getStackedBarWidth(xAxisData, config, xAxisConfig);
    const borderTopRadius = Math.min(config.borderTopRadius ?? 4, barWidth / 2);
    const borderBottomRadius = Math.min(config.borderBottomRadius ?? 0, barWidth / 2);
    const defs = svg.select('defs');

    data.forEach((d, idx) => {
      const uniqueId = getRuntimeUniqueId();
      const xStartPoint = xAxisData.scale(d[xAxisConfig.dataKey] as number) - barWidth / 2;

      const { barHeights, restSpace, barYs } = getBarData(d, config.bars, yScale);

      defs
        .append('clipPath')
        .attr('id', uniqueId)
        .append('path')
        .attr(
          'd',
          `
            M ${xStartPoint + borderBottomRadius} ${minYPoint}
            q ${-1 * borderBottomRadius},0 ${-1 * borderBottomRadius},${-1 * borderBottomRadius}
            v ${-1 * (minYPoint - restSpace - 1 * borderTopRadius - 1 * borderBottomRadius)}
            q 0,${-1 * borderTopRadius} ${borderTopRadius},${-1 * borderTopRadius}
            h ${barWidth - 2 * borderTopRadius}
            q ${borderTopRadius},0 ${borderTopRadius},${borderTopRadius}
            v ${minYPoint - restSpace - 1 * borderTopRadius - 1 * borderBottomRadius}
            q 0,${borderBottomRadius} ${-1 * borderBottomRadius},${borderBottomRadius}
            z
            `
        );

      const barsG = linesContainer.append('g').attr('clip-path', `url(#${uniqueId})`);

      config.bars.forEach((bar, idx) => {
        this.appendStackedBarSegment(barsG, bar, d, idx, xStartPoint, barYs[idx], barWidth, barHeights[idx]);
      });

      if (typeof config.striped === 'function' ? config.striped(d, idx) : config.striped) {
        const uniqueStripeId = getRuntimeUniqueId();

        linesContainer
          .append('pattern')
          .attr('id', uniqueStripeId)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('width', 10)
          .attr('height', 10)
          .append('path')
          .attr('d', 'M-1,1 l2,-2 M0,10 l10,-10 M9,21 l2,-2')
          .attr('class', 'stroke-foreground-primary-12')
          .style('stroke-width', 1);

        barsG
          .append('rect')
          .attr('x', xStartPoint)
          .attr('y', 0)
          .attr('width', Math.max(barWidth, 0))
          .attr('height', yScale(0))
          .attr('fill', `url(#${uniqueStripeId})`);
      }
    });
  }

  private getStackedBarWidth(xAxisData: XAxisData, config: ChartStackedBarConfig<Data>, xAxisConfig: ChartXAxisConfig<Data>): number {
    const compactDataPointLayout = xAxisConfig.compactDataPointLayout;
    if (compactDataPointLayout) {
      const requestedBandwidth = compactDataPointLayout.barWidth + compactDataPointLayout.gap;
      return xAxisData.bandwidth * (compactDataPointLayout.barWidth / requestedBandwidth);
    }

    const barPadding = config.barPadding ?? 2;
    const maxBarWidth = config.maxBarWidth ?? 16;
    return Math.min(xAxisData.bandwidth - 2 * barPadding, maxBarWidth);
  }

  public drawGroupedBarChart(
    data: Data[],
    svg: D3ChartSvg,
    linesContainer: D3ChartSvgg,
    xAxisData: XAxisData,
    yScale: D3YScale,
    config: ChartGroupedBarConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    if (data.length === 0) {
      return;
    }

    const minYPoint = yScale(yScale.domain()[0]);
    const maxYPoint = yScale(yScale.domain()[1]);
    const zeroYPoint = yScale(0);
    const requestedGroupPadding = config.groupPadding ?? 4;
    const requestedBarGap = config.barGap ?? 2;
    const minBarWidth = config.minBarWidth ?? 2;
    const gapCount = config.bars.length - 1;
    let groupPadding = requestedGroupPadding;
    let groupWidth = xAxisData.bandwidth - 2 * groupPadding;
    let barGap = requestedBarGap;

    if (groupWidth < config.bars.length * minBarWidth + gapCount * barGap) {
      barGap = gapCount > 0 ? Math.max(0, (groupWidth - config.bars.length * minBarWidth) / gapCount) : 0;

      if (groupWidth < config.bars.length * minBarWidth) {
        groupPadding = Math.max(1, (xAxisData.bandwidth - config.bars.length * minBarWidth) / 2);
        groupWidth = xAxisData.bandwidth - 2 * groupPadding;
      }
    }

    const borderRadius = config.borderRadius ?? 4;
    const barWidth = (groupWidth + barGap) / config.bars.length - barGap;
    const defs = svg.select('defs');

    data.forEach((d) => {
      const groupXStartPoint = xAxisData.scale(d[xAxisConfig.dataKey] as number) - groupWidth / 2;

      if (config.backgroundFillColorClassFn) {
        const fillColorClass = config.backgroundFillColorClassFn(d);

        if (fillColorClass) {
          this.drawBackgroundRect(d, xAxisData, linesContainer, xAxisConfig, minYPoint, maxYPoint, fillColorClass);
        }
      }

      config.bars.forEach((barConfig, index: number) => {
        const value = barConfig.dataKey ? (d[barConfig.dataKey] as number | null) : barConfig.dataMapper!(d, index);

        if (!isDefined(value)) {
          return;
        }

        const barXStartPoint = groupXStartPoint + index * (barWidth + barGap);

        const uniqueId = getRuntimeUniqueId();

        defs
          .append('clipPath')
          .attr('id', uniqueId)
          .append('rect')
          .attr('x', barXStartPoint)
          .attr('y', 0)
          .attr('width', Math.max(barWidth, 0))
          .attr('height', Math.max(minYPoint - maxYPoint, 0));

        const valueYPoint = yScale(value);
        const barHeight = Math.abs(zeroYPoint - valueYPoint);
        const barRadius = Math.min(borderRadius, barWidth / 2, barHeight);
        const barPath =
          value >= 0
            ? `
              M ${barXStartPoint} ${zeroYPoint}
              v ${-1 * (barHeight - barRadius)}
              q 0,${-1 * barRadius} ${barRadius},${-1 * barRadius}
              h ${barWidth - 2 * barRadius}
              q ${barRadius},0 ${barRadius},${barRadius}
              v ${barHeight - barRadius}
              z
              `
            : `
              M ${barXStartPoint} ${zeroYPoint}
              v ${barHeight - barRadius}
              q 0,${barRadius} ${barRadius},${barRadius}
              h ${barWidth - 2 * barRadius}
              q ${barRadius},0 ${barRadius},${-1 * barRadius}
              v ${-1 * (barHeight - barRadius)}
              z
              `;

        linesContainer.append('path').attr('d', barPath).attr('clip-path', `url(#${uniqueId})`).attr('class', barConfig.fillColorClass);
      });
    });
  }

  // Renders a single stacked-bar segment, colouring it by explicit hex (`fillColor`, takes
  // precedence) or by Tailwind class (`fillColorClass`).
  private appendStackedBarSegment(
    barsG: D3ChartSvgg,
    bar: ChartStackedBarBarConfig<Data>,
    d: Data,
    idx: number,
    x: number,
    y: number,
    width: number,
    height: number
  ): void {
    const rect = barsG.append('rect').attr('x', x).attr('y', y).attr('width', Math.max(width, 0)).attr('height', Math.max(height, 0));

    const fillColor = typeof bar.fillColor === 'function' ? bar.fillColor(d, idx) : bar.fillColor;

    if (fillColor) {
      rect.style('fill', fillColor);

      return;
    }

    const barColorClass = typeof bar.fillColorClass === 'function' ? bar.fillColorClass(d, idx) : bar.fillColorClass;
    rect.attr('class', barColorClass ?? '');
  }

  private drawBackgroundRect(
    d: Data,
    xAxisData: XAxisData,
    linesContainer: D3ChartSvgg,
    xAxisConfig: ChartXAxisConfig<Data>,
    minYPoint: number,
    maxYPoint: number,
    fillColorClass: string,
    config?: ChartBackgroundRectConfig<Data>
  ): void {
    if (fillColorClass) {
      const width = config?.width ?? xAxisData.bandwidth;
      const topOverflow = config?.topOverflow ?? 0;
      const bottomOverflow = config?.bottomOverflow ?? 0;
      const y = Math.min(minYPoint, maxYPoint) - topOverflow;
      const height = Math.abs(minYPoint - maxYPoint) + topOverflow + bottomOverflow;
      const borderRadius = Math.min(config?.borderRadius ?? 0, width / 2, height / 2);

      linesContainer
        .append('rect')
        .attr('x', xAxisData.scale(d[xAxisConfig.dataKey] as number) - width / 2)
        .attr('y', y)
        .attr('width', Math.max(width, 0))
        .attr('height', Math.max(height, 0))
        .attr('rx', borderRadius)
        .attr('ry', borderRadius)
        .attr('class', fillColorClass);
    }
  }
}
